import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Home, DollarSign, Sparkles, FileText, Cloud, AlertTriangle, Loader2, Zap, DoorOpen } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PageHeader } from '@/components/PageHeader';
import { AIDisclaimerBanner } from '@/components/AIDisclaimerBanner';
import { RoofMapView } from '@/components/RoofMapView';
import { StormIntelCard } from '@/components/StormIntelCard';
import api from '@/lib/api';
import { buildAutofillFromAI } from '@/lib/autofill';

export default function LeadDetailPage() {
    const { propertyKey } = useParams();
    const decodedKey = decodeURIComponent(propertyKey);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [instantLoading, setInstantLoading] = useState(false);
    const [aiEstimate, setAiEstimate] = useState(null);
    const roofMapRef = useRef(null);
    const navigate = useNavigate();

    const load = async () => {
        try {
            const r = await api.get(`/leads/${encodeURIComponent(decodedKey)}`);
            setData(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to load lead');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [decodedKey]);

    const createQuote = async () => {
        setCreating(true);
        try {
            const r = await api.post('/quotes', {
                property_key: decodedKey,
                homeowner_name: data?.property?.owner?.names?.join(', ') || '',
                property_address: `${data?.property?.addressLine1 || ''}, ${data?.property?.city || ''} ${data?.property?.state || ''} ${data?.property?.zipCode || ''}`.trim(),
            });
            toast.success('Quote draft created');
            navigate(`/app/quotes/${r.data.id}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to create quote');
        } finally {
            setCreating(false);
        }
    };

    // ✨ The "Loom magic moment" — type-an-address → instant AI quote.
    // Creates a draft quote, runs AI vision on the current map view, then opens
    // the QuoteBuilder on the Roof step pre-filled with the estimate.
    const instantQuote = async () => {
        if (!roofMapRef.current) { toast.error('Map not ready yet — give it a sec.'); return; }
        const state = roofMapRef.current.getMapState();
        if (!state) { toast.error('Map not centered. Refresh and try again.'); return; }
        setInstantLoading(true);
        try {
            // 1. Create the quote draft (pre-fills address + homeowner name from RentCast)
            const ownerNames = data?.property?.owner?.names || [];
            const fullAddr = `${data?.property?.addressLine1 || ''}, ${data?.property?.city || ''} ${data?.property?.state || ''} ${data?.property?.zipCode || ''}`.trim();
            const draftR = await api.post('/quotes', {
                property_key: decodedKey,
                homeowner_name: ownerNames.join(', ') || '',
                property_address: fullAddr,
            });
            const quoteId = draftR.data.id;

            // 2. Run AI Vision on the current map view
            const estR = await api.post(`/quotes/${quoteId}/roof/estimate`, {
                lat: state.center[1],
                lng: state.center[0],
                zoom: state.zoom,
                address: fullAddr,
                year_built: data?.property?.yearBuilt || undefined,
                sqft: data?.property?.squareFootage || undefined,
            }, { timeout: 60000 });
            setAiEstimate(estR.data?.estimate || null);

            // 3. Auto-apply materials + costs in the same flow so the editor opens
            //    with the WHOLE quote populated. Org's pricing profile feeds in here;
            //    materials swap based on what AI saw (asphalt vs metal vs tile).
            try {
                const profRes = await api.get('/me/pricing-profile').catch(() => null);
                const patch = buildAutofillFromAI({
                    measurements: estR.data?.measurements || estR.data?.estimate,
                    pricingProfile: profRes?.data?.profile,
                    stories: 1,  // sensible default; user can change in editor
                });
                if (patch.materials && patch.costs) {
                    await api.patch(`/quotes/${quoteId}`, {
                        materials: patch.materials,
                        costs: patch.costs,
                    });
                }
            } catch (_e) {
                // Cost auto-fill is best-effort. If pricing profile fails to load,
                // the editor still opens with AI measurements present and the
                // roofer can re-trigger Auto-fill manually.
            }

            toast.success('Quote ready — opening estimate…');
            // 4. Jump straight to the (now fully-populated) EstimateBuilder
            navigate(`/app/quotes/${quoteId}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Instant quote failed');
        } finally {
            setInstantLoading(false);
        }
    };

    if (loading) return <div className="text-slate-500">Loading…</div>;
    if (!data) return <div className="text-slate-500">Lead not found</div>;

    const p = data.property || {};
    const alerts = data.storm_alerts || [];
    const unlock = data.unlock || {};

    return (
        <>
            <div className="mb-4">
                <Button asChild variant="ghost" size="sm" data-testid="lead-back-button">
                    <Link to="/app/leads"><ArrowLeft className="h-4 w-4 mr-1" /> Back to Lead Finder</Link>
                </Button>
            </div>
            <PageHeader
                title={p.addressLine1 || 'Lead'}
                subtitle={`${p.city || ''}, ${p.state || ''} ${p.zipCode || ''}`}
                testId="lead-detail-header"
                actions={
                    <div className="flex flex-wrap items-center gap-2">
                        <Button
                            onClick={instantQuote}
                            disabled={instantLoading || creating}
                            className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"
                            data-testid="lead-instant-quote-button"
                        >
                            {instantLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Zap className="h-4 w-4 mr-1" />}
                            {instantLoading ? 'Generating…' : 'Instant AI quote'}
                        </Button>
                        <button
                            type="button"
                            onClick={createQuote}
                            disabled={creating || instantLoading}
                            className="text-xs text-slate-500 hover:text-slate-800 underline-offset-2 hover:underline disabled:opacity-50"
                            data-testid="lead-create-quote-button"
                        >
                            {creating ? 'Creating…' : 'Start a blank quote instead'}
                        </button>
                    </div>
                }
            />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 space-y-4">
                    <Card>
                        <CardHeader>
                            <CardTitle className="font-display flex items-center justify-between flex-wrap gap-2">
                                <span className="flex items-center gap-2"><Zap className="h-5 w-5 text-amber-500" /> Roof &amp; AI estimator</span>
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <RoofMapView
                                ref={roofMapRef}
                                address={`${p.addressLine1 || ''}, ${p.city || ''} ${p.state || ''} ${p.zipCode || ''}`.trim()}
                                readOnly={false}
                                height="360px"
                            />
                            {aiEstimate && (
                                <div className="mt-3 border border-amber-200 bg-amber-50/60 rounded-lg p-3 space-y-2" data-testid="lead-ai-result">
                                    <div className="flex items-center justify-between flex-wrap gap-2">
                                        <div className="font-display font-semibold text-slate-900 flex items-center gap-2">
                                            <Sparkles className="h-4 w-4 text-amber-600" /> AI Vision result
                                        </div>
                                        <Badge variant="outline" className={`font-normal capitalize ${aiEstimate.confidence === 'high' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : aiEstimate.confidence === 'medium' ? 'bg-amber-50 text-amber-800 border-amber-200' : 'bg-rose-50 text-rose-700 border-rose-200'}`}>
                                            {aiEstimate.confidence || 'low'} confidence
                                        </Badge>
                                    </div>
                                    <div className="text-sm text-slate-700">
                                        <b>{aiEstimate.facets_count || '—'}</b> facets · pitch <b className="capitalize">{aiEstimate.pitch || '—'}</b> · footprint <b>{aiEstimate.roof_footprint_sqft_min?.toLocaleString() || '—'}–{aiEstimate.roof_footprint_sqft_max?.toLocaleString() || '—'} sqft</b>
                                    </div>
                                    {aiEstimate.notes && <p className="text-xs text-slate-600 italic">{aiEstimate.notes}</p>}
                                </div>
                            )}
                            <AIDisclaimerBanner variant="estimate" className="mt-3" />
                            <div className="mt-3 text-xs text-slate-500">
                                Drag &amp; zoom the map onto the homeowner&apos;s roof, then hit <b className="text-slate-900">Instant AI quote</b> above to auto-draft a quote with AI measurements.
                            </div>
                        </CardContent>
                    </Card>

                    {/* Storm intelligence — surfaces NOAA LSR data within a
                        10-mile radius. Strongest selling point for storm-chasers
                        and a great talking point to use during the door knock. */}
                    {(() => {
                        const stormLat = Number(p.latitude || p._geocoded_lat);
                        const stormLng = Number(p.longitude || p._geocoded_lng);
                        if (!stormLat || !stormLng) return null;
                        return (
                            <StormIntelCard
                                lat={stormLat}
                                lng={stormLng}
                                state={p.state}
                                radiusMiles={10}
                                daysBack={730}
                                title="Recent severe weather near this property"
                            />
                        );
                    })()}

                    <Card>
                        <CardHeader><CardTitle className="font-display">Property details</CardTitle></CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
                                <div><div className="text-slate-500">Year built</div><div className="font-medium">{p.yearBuilt || '—'}</div></div>
                                <div><div className="text-slate-500">Type</div><div className="font-medium">{p.propertyType || '—'}</div></div>
                                <div><div className="text-slate-500">Sq ft</div><div className="font-medium">{p.squareFootage ? p.squareFootage.toLocaleString() : '—'}</div></div>
                                <div><div className="text-slate-500">Lot size</div><div className="font-medium">{p.lotSize ? `${p.lotSize.toLocaleString()} sqft` : '—'}</div></div>
                                <div><div className="text-slate-500">Beds / Baths</div><div className="font-medium">{p.bedrooms || '—'} / {p.bathrooms || '—'}</div></div>
                                <div><div className="text-slate-500">Last sale</div><div className="font-medium">{p.lastSalePrice ? `$${Number(p.lastSalePrice).toLocaleString()}` : '—'}</div></div>
                                <div><div className="text-slate-500">Last sale date</div><div className="font-medium">{p.lastSaleDate?.slice(0,10) || '—'}</div></div>
                                <div><div className="text-slate-500">Roof age (est.)</div><div className="font-medium">{p.yearBuilt ? `${new Date().getFullYear() - p.yearBuilt} yrs` : '—'}</div></div>
                            </div>
                            {p.owner && (
                                <div className="mt-4 border-t pt-4">
                                    <div className="text-slate-500 text-xs uppercase tracking-wider">Owner</div>
                                    <div className="font-medium">{(p.owner.names || []).join(', ') || '—'}</div>
                                    {p.owner.mailingAddress && (
                                        <div className="text-sm text-slate-600">{p.owner.mailingAddress.formattedAddress || ''}</div>
                                    )}
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    <Card className="border-slate-200">
                        <CardHeader>
                            <CardTitle className="font-display flex items-center gap-2 text-base">
                                <DoorOpen className="h-4 w-4 text-amber-600" /> Next steps
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <p className="text-sm text-slate-600">
                                You&apos;ve got the property&apos;s public-records context above. The job&apos;s still won
                                door-to-door &mdash; pictures, a real conversation, and a fast accurate quote.
                            </p>
                            <ol className="mt-3 space-y-2 text-sm text-slate-700">
                                <li className="flex gap-2"><span className="font-semibold text-amber-700 shrink-0">1.</span> Visit the property &mdash; check the roof from the ground or with a drone.</li>
                                <li className="flex gap-2"><span className="font-semibold text-amber-700 shrink-0">2.</span> Talk to the homeowner. Take photos of damage / wear if invited.</li>
                                <li className="flex gap-2"><span className="font-semibold text-amber-700 shrink-0">3.</span> Come back here, click <b>Instant AI quote</b>, and download the PDF to hand-deliver, text, or share via the public link.</li>
                            </ol>
                            <p className="mt-3 text-xs text-slate-500 italic">
                                QuotesFlow doesn&apos;t send cold emails on your behalf. You&apos;re always responsible
                                for following CAN-SPAM, TCPA, and your local Do-Not-Knock rules.
                            </p>
                        </CardContent>
                    </Card>
                </div>

                <div className="space-y-4">
                    {/* Active NWS alerts — only shown when there's something
                        currently happening. The historical 24-month storm card
                        in the left column already covers the "did it get hit
                        recently" question, so a "no current alerts" empty state
                        here would just be noise. */}
                    {alerts.length > 0 && (
                        <Card className="border-amber-200 bg-amber-50/40">
                            <CardHeader>
                                <CardTitle className="font-display flex items-center gap-2">
                                    <AlertTriangle className="h-4 w-4 text-amber-600" /> Active NWS alerts
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <ul className="space-y-3">
                                    {alerts.slice(0, 4).map((a) => (
                                        <li key={a.id} className="border-l-2 border-amber-500 pl-3 py-1">
                                            <div className="font-medium text-slate-900 text-sm flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5 text-amber-600" /> {a.event}</div>
                                            <div className="text-xs text-slate-500">Severity: {a.severity}</div>
                                            <div className="text-xs text-slate-600 mt-0.5">{(a.area_desc || '').slice(0, 100)}</div>
                                        </li>
                                    ))}
                                </ul>
                            </CardContent>
                        </Card>
                    )}
                    <Card>
                        <CardHeader><CardTitle className="font-display text-base">Lead status</CardTitle></CardHeader>
                        <CardContent>
                            <div className="flex flex-col gap-2">
                                <Badge variant="outline" className="w-fit">{unlock.status || 'new'}</Badge>
                                <div className="text-xs text-slate-500">Unlocked {unlock.created_at?.slice(0, 10) || ''}</div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </>
    );
}
